import { gql, useQuery } from "@apollo/client";
import useCheckDevice from "@repo/shopify/hooks/useCheckDevice";
import type { IProductDetailQuery } from "@repo/shopify/queries/product-gql";
import type { ISearchEdge, ISearchQuery } from "@repo/shopify/queries/search-gql";
import { searchQuery } from "@repo/shopify/queries/search-gql";
import type { Product, ProductVariantConnection } from "@shopify/hydrogen-react/storefront-api-types";
import qs from "query-string";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import Button from "@atoms/Button";
import Icon from "@atoms/Icon";
import Loading from "@atoms/Loading";

import Filter from "@molecules/Filter";
import ProductCard from "@molecules/ProductCard";

import { SEARCH_SORT } from "@const/constant";
import { DEFAULT_TITLE } from "@const/meta";
import { NOTIFICATIONS } from "@const/notifications";
import { URLS } from "@const/urls";

import useHeaderScroll from "@hooks/useHeaderScroll";

import { useLayoutStore } from "@store/layout";

export default function SearchResult() {
  const navigate = useNavigate();
  const { search } = useLocation();

  const [searchParams] = useSearchParams();

  const { isPC } = useCheckDevice();

  const queryParameter = searchParams.get("q");

  useHeaderScroll();

  const queryString = qs.parse(search);

  const loaderEl = useRef(null);

  const { openToast, showFooter, setMeta, clearMeta, openSearch } = useLayoutStore();

  const [searchData, setSearchData] = useState<ISearchQuery["search"] | undefined>();
  const [products, setProducts] = useState<ISearchEdge[]>([]);
  const [expandProducts, setExpandProducts] = useState<ISearchEdge[]>([]);

  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [hasDim, setHasDim] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);

  const prevFilter = useRef<typeof variables.productFilters>([]);
  const isTriggeredRef = useRef(false);
  const productLength = useRef(0); // 이전 products의 길이 저장

  useEffect(() => {
    if (!queryParameter) {
      navigate(URLS.HOME);
      setTimeout(() => {
        openSearch(true);
      }, 500);
    }
  }, []);

  // 검색어 변경 될때 초기화 해줘야 함
  useEffect(() => {
    setExpandProducts([]);
    productLength.current = 0;
    setSearchData(undefined);
    setShowFilter(false);
    window.scrollTo(0, 0);
  }, [queryParameter]);

  const variables = useMemo(() => {
    setExpandProducts([]);
    productLength.current = 0;

    const sortQueries = SEARCH_SORT.find((item) => item.name === queryString.sort);

    const filterQueries = Object.entries(queryString)
      .filter(([key]) => key === "category" || key === "color" || key === "size")
      .map(([key, value]) => {
        const parseValue = JSON.parse((value as string) || "[]") as string[];
        return parseValue.map((item) =>
          key === "category" ? { productType: item } : { variantOption: { name: key, value: item } },
        );
      })
      .flat();

    return {
      first: Object.keys(queryString).includes("view") && queryString.view === "expand" ? 12 : 24, // 쿼리 1회에 조회하는 product 수
      types: "PRODUCT",
      query: queryParameter,
      sortKey: sortQueries?.value ?? "RELEVANCE",
      reverse: sortQueries?.reverse ?? true,
      prefix: "LAST",
      productFilters: filterQueries || [],
    };
  }, [search, queryParameter]);

  const { loading, fetchMore, refetch } = useQuery<ISearchQuery>(gql(searchQuery.query), {
    variables,
    notifyOnNetworkStatusChange: true,
    onCompleted: (_data) => {
      if (!_data.search) {
        navigate("/404");
        return;
      }

      prevFilter.current = variables.productFilters;

      const searchList =
        (_data?.search?.edges as ISearchEdge[])
          ?.filter(({ node }) => Boolean(!(node as IProductDetailQuery["product"])?.vendor.includes("GWP")))
          ?.filter(({ node }) => (node as IProductDetailQuery["product"])?.hidden?.value !== "true") || [];

      setSearchData(_data?.search);
      setProducts(searchList);

      if (Object.keys(queryString).includes("view") && queryString.view === "expand") {
        setIsLoading(true);

        const newExpandList = getExpandList(searchList.slice(productLength.current));
        setExpandProducts([...expandProducts, ...newExpandList]);

        productLength.current = searchList.length;

        setIsLoading(false);
      }
    },
    onError: (error) => {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      throw new Error(error.message);
    },
  });

  useEffect(() => {
    if (!loading) {
      setTimeout(() => {
        setHasDim(false);
      }, 300);
    }
  }, [loading, hasDim]);

  const getExpandList = (searchList: ISearchEdge[]) => {
    const expandList: ISearchEdge[] = [];

    searchList.forEach(({ node }) => {
      const colorOptions = (node as Product).options.find((item) => item.name === "Color")?.values;

      if (colorOptions) {
        colorOptions.forEach((color) => {
          const colorList = (node as Product).variants.edges.filter(({ node: variantNode }) =>
            variantNode.selectedOptions.find((option) => option.value === color),
          );

          const newList: ISearchEdge = {
            node: {
              ...node,
              options: (node as Product).options.map((option) =>
                option.name === "Color" ? { ...option, values: [color] } : option,
              ),
              variants: { edges: colorList } as ProductVariantConnection,
            },
          } as ISearchEdge;

          expandList.push(newList);
        });
      }
    });

    return expandList;
  };

  const hasNextPage = useMemo(() => searchData?.pageInfo.hasNextPage || false, [searchData?.pageInfo]);
  const endCursor = useMemo(() => searchData?.pageInfo.endCursor || "", [searchData?.pageInfo]);

  useEffect(() => {
    showFooter(!hasNextPage);
  }, [hasNextPage]);

  const refetchData = async () => {
    const { data: _data } = await refetch();
    if (_data) {
      const searchList =
        _data?.search?.edges
          ?.filter(({ node }: { node: IProductDetailQuery["product"] }) => Boolean(!node?.vendor.includes("GWP")))
          ?.filter(({ node }) => node?.hidden?.value !== "true") || [];

      setProducts(searchList);

      if (Object.keys(queryString).includes("view") && queryString.view === "expand") {
        setIsLoading(true);

        const newExpandList = getExpandList(searchList);
        setExpandProducts(newExpandList);

        productLength.current = searchList.length;

        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (JSON.stringify(prevFilter.current) !== JSON.stringify(variables.productFilters)) {
      void refetchData();
    }
  }, [variables.productFilters]);

  useEffect(() => {
    setMeta({ title: `Search Result for "${queryParameter}" - ${DEFAULT_TITLE}` });

    return () => {
      clearMeta();
    };
  }, [queryParameter]);

  /**
   * InterSectionObserver handler
   * 컬렉션 데이터 추가 쿼리
   * */
  const handleObserver = useCallback(
    async (entries: IntersectionObserverEntry[]) => {
      if (isTriggeredRef.current) return;

      const target = entries[0];

      if (target.isIntersecting && hasNextPage) {
        isTriggeredRef.current = true;

        await fetchMore({
          variables: { ...variables, after: endCursor },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prev;

            return {
              search: {
                ...prev.search,
                edges: [...prev.search.edges, ...fetchMoreResult.search.edges],
                pageInfo: fetchMoreResult.search.pageInfo,
              },
            };
          },
        });

        isTriggeredRef.current = false;
      }
    },
    [isTriggeredRef, endCursor, hasNextPage, variables, loaderEl],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "1000px 0px 0px 0px",
      threshold: 0.1,
    });

    if (loaderEl?.current) observer.observe(loaderEl.current);

    return () => {
      observer.disconnect();
    };
  }, [handleObserver, loaderEl]);

  const showProductList = useMemo(
    () => (Object.keys(queryString).includes("view") && queryString.view === "expand" ? expandProducts : products),
    [queryString, expandProducts, products],
  );

  return (
    <>
      {!searchData && (loading || isLoading) ? (
        <Loading type="page" />
      ) : (
        <>
          {isPC ? (
            <section className="pc:col-span-2 pc:gap-5 pc:w-screen-col-span-2 sticky top-36 left-5 col-span-8 flex max-h-dvh gap-2 self-start overflow-y-auto pb-46">
              {showFilter ? (
                <Filter
                  filters={searchData?.productFilters || []}
                  onClickClose={() => {
                    setShowFilter(false);
                  }}
                />
              ) : (
                <div className="flex flex-col gap-10">
                  <Button
                    as="text"
                    className="justify-end"
                    onClick={() => {
                      setShowFilter(!showFilter);
                    }}
                  >
                    Filter & Sort
                  </Button>
                  {/*<div className="flex flex-col gap-2">*/}
                  {/*  {menuData?.menu?.items.map((item) => (*/}
                  {/*    <React.Fragment key={item.id}>*/}
                  {/*      {item.title !== "Archives" && (*/}
                  {/*        <div className="tnt-gap-x pc:col-start-1 col-span-8 col-start-2 flex">*/}
                  {/*          <Link*/}
                  {/*            as="text"*/}
                  {/*            className={classNames(*/}
                  {/*              "pc:col-span-1 col-span-5 w-fit leading-none",*/}
                  {/*              item.title === "Archives" && "pc:pt-2.5 pt-3.5",*/}
                  {/*              !isPC && "text-base!",*/}
                  {/*            )}*/}
                  {/*            href={`/${item.url?.replace(domain, "").replace(shopifyDomain, "")}`}*/}
                  {/*          >*/}
                  {/*            {`${item.title}${item.title === "Archives" ? ":" : ""}`}*/}
                  {/*          </Link>*/}
                  {/*        </div>*/}
                  {/*      )}*/}

                  {/*      {item.title === "Archives" && (*/}
                  {/*        <>*/}
                  {/*          {isPC ? (*/}
                  {/*            <>*/}
                  {/*              <Link*/}
                  {/*                as="text"*/}
                  {/*                className="col-span-1 w-fit leading-none"*/}
                  {/*                href={`/${item.url?.replace(domain, "").replace(shopifyDomain, "")}`}*/}
                  {/*              >*/}
                  {/*                Archives:*/}
                  {/*              </Link>*/}
                  {/*              {item.items.length ? (*/}
                  {/*                <div className="col-span-2 col-start-1 flex w-fit flex-col gap-2.5">*/}
                  {/*                  <div className="ml-5 flex flex-col gap-2 dark:border-white">*/}
                  {/*                    {item.items.map((subItem) => (*/}
                  {/*                      <Link*/}
                  {/*                        as="text"*/}
                  {/*                        className={classNames("w-fit leading-none", !isPC && "text-base!")}*/}
                  {/*                        href={`/${subItem.url?.replace(domain, "").replace(shopifyDomain, "")}`}*/}
                  {/*                        key={subItem.id}*/}
                  {/*                      >*/}
                  {/*                        {subItem.title}*/}
                  {/*                      </Link>*/}
                  {/*                    ))}*/}
                  {/*                  </div>*/}
                  {/*                </div>*/}
                  {/*              ) : null}*/}
                  {/*            </>*/}
                  {/*          ) : (*/}
                  {/*            <div className="col-start-2 flex gap-2">*/}
                  {/*              <Link*/}
                  {/*                as="text"*/}
                  {/*                className="col-span-1 w-fit text-base! leading-none"*/}
                  {/*                href={`/${item.url?.replace(domain, "").replace(shopifyDomain, "")}`}*/}
                  {/*              >*/}
                  {/*                Archives:*/}
                  {/*              </Link>*/}

                  {/*              {item.items.length ? (*/}
                  {/*                <div className="col-span-5 col-start-2 flex w-fit flex-col gap-2.5">*/}
                  {/*                  <div className="flex flex-col gap-3.5 dark:border-white">*/}
                  {/*                    {item.items.map((subItem) => (*/}
                  {/*                      <Link*/}
                  {/*                        as="text"*/}
                  {/*                        className={classNames("w-fit leading-none", !isPC && "text-base!")}*/}
                  {/*                        href={`/${subItem.url?.replace(domain, "").replace(shopifyDomain, "")}`}*/}
                  {/*                        key={subItem.id}*/}
                  {/*                      >*/}
                  {/*                        {subItem.title}*/}
                  {/*                      </Link>*/}
                  {/*                    ))}*/}
                  {/*                  </div>*/}
                  {/*                </div>*/}
                  {/*              ) : null}*/}
                  {/*            </div>*/}
                  {/*          )}*/}
                  {/*        </>*/}
                  {/*      )}*/}
                  {/*    </React.Fragment>*/}
                  {/*  ))}*/}
                  {/*</div>*/}
                </div>
              )}
            </section>
          ) : (
            <>
              {showFilter ? (
                <Filter
                  filters={searchData?.productFilters || []}
                  onClickClose={() => {
                    setShowFilter(false);
                  }}
                />
              ) : (
                <div
                  className="sticky top-0 z-20 col-span-8 flex w-full justify-between gap-1 self-start bg-white py-2.5"
                  id="filter"
                >
                  <h1 className="text-title leading-none">{`Search Result for "${queryParameter}"`}</h1>
                  <Button
                    as="wrapper"
                    onClick={() => {
                      setShowFilter(true);
                    }}
                  >
                    <Icon name="Filter" />
                  </Button>
                </div>
              )}
            </>
          )}

          <section className="pc:col-span-10 pc:grid-cols-5 pc:col-start-3 col-span-8 flex flex-col gap-2.5">
            {isPC ? <h1 className="text-title leading-none">{`Search Result for "${queryParameter}"`}</h1> : null}
            {showProductList.length ? (
              <section className="tnt-gap-x pc:col-span-10 pc:grid-cols-5 pc:gap-y-20 relative col-span-8 grid grid-cols-2 gap-y-5">
                {showProductList.map(({ node }, index) => (
                  <ProductCard
                    color={node.options.find((item) => item.name === "Color")?.values || []}
                    key={`${node.id}_${index}`}
                    releaseDate={node?.release_date}
                    tags={node.tags}
                    title={node.title}
                    variants={node.variants}
                  />
                ))}
              </section>
            ) : (
              <>{!loading ? <span>There is no result.</span> : null}</>
            )}

            <div ref={loaderEl} />

            {hasNextPage ? <Loading /> : null}

            {showProductList.length && !hasNextPage ? (
              <Button
                as="text"
                className="pc:col-span-12 col-span-8 mt-10 flex place-self-center"
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                Back to Top
              </Button>
            ) : null}
          </section>
        </>
      )}
    </>
  );
}
